import React from 'react';
import {
  Box, Button, CircularProgress, Typography, Dialog, DialogTitle,
  DialogContent, DialogActions, List, ListItem, ListItemButton,
  ListItemText, IconButton, TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';

function catalogPlanIsDigitalCarrier(p) {
  const dm = p?.digital_marketing_features;
  if (Array.isArray(dm) && dm.length > 0) return true;
  const st = String(p?.service_type || '').toLowerCase();
  if (st === 'digital_marketing') return true;
  const svc = String(p?.service_name || '').toLowerCase();
  const nm = String(p?.name || '').toLowerCase();
  if (svc.includes('digital marketing')) return true;
  if ((svc.includes('digital') || nm.includes('digital')) && !svc.includes('erp')) return true;
  return false;
}

function pickErpPlan(plans) {
  if (!plans?.length) return null;
  const erpSvc = (p) => String(p?.service_name || '').trim().toLowerCase();
  const erpNm = (p) => String(p?.name || '').trim().toLowerCase();
  const erpCandidatesFiltered = plans.filter(
    (p) => !catalogPlanIsDigitalCarrier(p) && erpSvc(p).includes('erp'),
  );
  const scoreErpPlan = (p) => {
    const s = erpSvc(p);
    const n = erpNm(p);
    let score = 0;
    if (s === 'erp') score += 200;
    if (n === 'erp') score += 80;
    if (s.endsWith(' erp')) score += 20;
    if (/\bnew\s*erp\b|^newerp$/i.test(s) || /\bnew\s*erp\b|^newerp$/i.test(n)) score -= 100;
    if (/\bstudio\b|\bmarketing\b|\bdigital\b/.test(`${s} ${n}`)) score -= 50;
    return score - Number(p.id || 0) / 1000000000;
  };
  const erpPlan =
    (erpCandidatesFiltered.length
      ? [...erpCandidatesFiltered].sort((a, b) => scoreErpPlan(b) - scoreErpPlan(a))[0]
      : null) ||
    plans.find((p) => !catalogPlanIsDigitalCarrier(p)) ||
    plans[0];
  return erpPlan;
}

function pickIdCardPlan(plans, erpFallback) {
  if (!plans?.length) return null;
  const found = plans.find((p) => {
    const serviceName = String(p?.service_name || '').toLowerCase();
    const planName = String(p?.name || '').toLowerCase();
    const blob = `${serviceName} ${planName}`;
    return (
      blob.includes('id card')
      || blob.includes('idcard')
      || blob.includes('identity card')
    );
  });
  return found || erpFallback || plans[0];
}

function pickBiometricPlan(plans, erpFallback) {
  if (!plans?.length) return null;
  const found = plans.find((p) => {
    const serviceName = String(p?.service_name || '').toLowerCase();
    const planName = String(p?.name || '').toLowerCase();
    const blob = `${serviceName} ${planName}`;
    return blob.includes('biometric');
  });
  return found || erpFallback || plans[0];
}

function pickDigitalCarrierPlan(plans) {
  if (!plans?.length) return null;
  const isDigitalCarrier = (p) => {
    const dm = p?.digital_marketing_features;
    if (Array.isArray(dm) && dm.length > 0) return true;
    const svcName = String(p?.service_name || p?.service?.name || '').toLowerCase();
    const name = String(p?.name || '').toLowerCase();
    const billingName = String(p?.billing_name || '').toLowerCase();
    return svcName.includes('digital') || name.includes('digital') || billingName.includes('digital');
  };
  // Prefer a true digital carrier plan; never fall back to an ERP plan for other services.
  const digitalPlan = plans.find(isDigitalCarrier);
  if (digitalPlan) return digitalPlan;
  // If no digital carrier plan found, pick the first non-ERP plan available.
  const nonErp = plans.find(p => {
    const svcName = String(p?.service_name || '').toLowerCase();
    const name = String(p?.name || '').toLowerCase();
    return !svcName.includes('erp') && !name.includes('erp');
  });
  return nonErp || plans[0];
}

/**
 * Add service line: user picks ERP, ID Cards, or a digital other service — no separate "plan" step.
 * Backend still receives a catalog plan_id (auto-resolved).
 */
function ServiceSelectionModal({ open, onClose, onSelect, props }) {
  const [loading, setLoading] = React.useState(false);
  const [resolving, setResolving] = React.useState(false);
  const [allPlans, setAllPlans] = React.useState([]);
  const [digitalServices, setDigitalServices] = React.useState([]);
  const [serviceSearch, setServiceSearch] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setServiceSearch('');
      loadData();
    }
  }, [open]);

  const loadData = () => {
    setLoading(true);
    const digitalUrl = GET_URL.digitalmarketingfeature?.api;
    Promise.allSettled([
      getRequest(GET_URL.plans?.api, {}, props),
      digitalUrl ? getRequest(digitalUrl, {}, props) : Promise.resolve(null),
    ]).then(([plansRes, digRes]) => {
      setLoading(false);
      const plansOk = plansRes?.status === 'fulfilled' && plansRes.value?.status === 200;
      const digOk = digRes?.status === 'fulfilled' && digRes.value?.status === 200;
      const plansList = plansOk ? (plansRes.value.data?.data || plansRes.value.data || []) : [];
      const digRaw = digOk ? (digRes.value.data?.data || digRes.value.data || []) : [];
      setAllPlans(Array.isArray(plansList) ? plansList : []);
      setDigitalServices(Array.isArray(digRaw) ? digRaw : []);
    }).catch(() => {
      setLoading(false);
    });
  };

  const serviceOptions = React.useMemo(() => {
    const base = [
      { __type: 'ERP', id: 'ERP', name: 'ERP', subtitle: 'ERP licence' },
      { __type: 'ID_CARDS', id: 'ID_CARDS', name: 'ID Cards', subtitle: 'Staff / Student ID cards with item-wise pricing' },
      { __type: 'BIOMETRIC_AUTO', id: 'BIOMETRIC_AUTO', name: 'Auto Biometric', subtitle: 'Devices, fee, and renewable fee' },
      { __type: 'BIOMETRIC_MANUAL', id: 'BIOMETRIC_MANUAL', name: 'Manual Biometric', subtitle: 'Devices, fee, and renewable fee' },
    ];
    const digital = (digitalServices || []).map((d) => ({
      __type: 'DIGITAL',
      id: `DIGITAL_${d.id}`,
      featureId: d.id,
      name: d.name,
      subtitle: 'Other service (digital)',
      calculation_type: d.calculation_type,
      description: d.description,
      price: d.price,
      renewal_price: d.renewal_price,
    }));
    return [...base, ...digital];
  }, [digitalServices]);

  const filteredOptions = React.useMemo(() => {
    if (!serviceSearch.trim()) return serviceOptions;
    const q = serviceSearch.toLowerCase().trim();
    return serviceOptions.filter(
      (o) =>
        o.name?.toLowerCase().includes(q)
        || o.subtitle?.toLowerCase().includes(q)
        || String(o.id).toLowerCase().includes(q),
    );
  }, [serviceOptions, serviceSearch]);

  const planServiceId = (plan) => {
    const s = plan?.service;
    if (s != null && typeof s === 'object') return s.id;
    return s;
  };

  const finishSelect = (servicePayload, plan, meta = {}) => {
    if (!plan?.id) return;
    const sid = planServiceId(plan);
    onSelect({
      service: { ...servicePayload, id: sid != null ? sid : servicePayload.id },
      plan,
      ...meta,
    });
    setServiceSearch('');
    onClose();
  };

  const handlePick = (option) => {
    if (!allPlans.length) return;
    setResolving(true);
    try {
      if (option.__type === 'ERP') {
        const plan = pickErpPlan(allPlans);
        if (!plan) throw new Error('No ERP plan in catalog');
        finishSelect(
          {
            id: plan.service,
            name: 'ERP',
            code: 'ERP',
          },
          plan,
        );
        return;
      }
      if (option.__type === 'ID_CARDS') {
        const erpFallback = pickErpPlan(allPlans);
        const plan = pickIdCardPlan(allPlans, erpFallback);
        if (!plan) throw new Error('No ID Card plan in catalog');
        finishSelect(
          {
            id: plan.service,
            name: 'ID Cards',
            code: 'ID_CARDS',
          },
          plan,
          { idCardSetup: true },
        );
        return;
      }
      if (option.__type === 'BIOMETRIC_AUTO' || option.__type === 'BIOMETRIC_MANUAL') {
        const erpFallback = pickErpPlan(allPlans);
        const plan = pickBiometricPlan(allPlans, erpFallback);
        if (!plan) throw new Error('No Biometric plan in catalog');
        const kind = option.__type === 'BIOMETRIC_MANUAL' ? 'manual' : 'auto';
        finishSelect(
          {
            id: plan.service,
            name: option.name,
            code: option.__type,
          },
          plan,
          { biometricSetup: true, biometricKind: kind },
        );
        return;
      }
      if (option.__type === 'DIGITAL') {
        const plan = pickDigitalCarrierPlan(allPlans);
        if (!plan?.id) throw new Error('No service plan found for digital services');
        finishSelect(
          {
            id: plan.service,
            name: option.name,
            digitalservice: Number(option.featureId),
            calculation_type: option.calculation_type || 'monthwise',
            digital_calculation_type: option.calculation_type || 'monthwise',
            // Use the digital feature's configured price, not the carrier plan's base_price.
            price: option.price != null && option.price !== '' ? option.price : null,
          },
          plan,
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setResolving(false);
    }
  };

  const busy = loading || resolving;

  return (
    <Dialog
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">Add service</Typography>
        <Typography variant="caption" color="textSecondary">
          Choose ERP, ID Cards, Auto/Manual Biometric, or another digital service. Pricing template is applied automatically.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search…"
            value={serviceSearch}
            onChange={(e) => setServiceSearch(e.target.value)}
            disabled={busy}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: serviceSearch && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setServiceSearch('')} disabled={busy}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {serviceSearch && (
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                Found {filteredOptions.length} services
              </Typography>
            )}
            <List sx={{ maxHeight: 380, overflow: 'auto' }}>
              {filteredOptions.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary={serviceSearch ? 'No matching services' : 'No services available'}
                    secondary={serviceSearch ? `Nothing matches "${serviceSearch}"` : 'Add catalog plans or digital features first'}
                  />
                </ListItem>
              ) : (
                filteredOptions.map((option) => (
                  <ListItem key={option.id} disablePadding>
                    <ListItemButton
                      onClick={() => handlePick(option)}
                      disabled={busy}
                      sx={{
                        '&:hover': { backgroundColor: '#e3f2fd' },
                        borderRadius: 1,
                        mb: 0.5,
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight="500">
                            {option.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="textSecondary">
                            {option.subtitle}
                            {option.description ? ` · ${option.description}` : ''}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
            {resolving && (
              <Box display="flex" justifyContent="center" py={1}>
                <CircularProgress size={28} />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={busy}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ServiceSelectionModal;
